import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  IoArrowUndoOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoCopyOutline,
  IoMailOutline,
  IoPersonOutline,
  IoTicketOutline,
  IoTimeOutline,
} from 'react-icons/io5'
import moment from 'moment'
import toast from 'react-hot-toast'
import Box from '@components/base/Box'
import LoadingSpinner from '@components/base/LoadingSpinner'
import Modal from '@components/base/Modal'
import ProfileImage from '@components/base/ProfileImage'
import { Button } from '@components/inputs/Button'
import { InputPicker } from '@components/inputs/InputPicker/InputPicker'
import {
  EventAttendee,
  FormInputType,
  RegistrationField,
  TicketOrder,
} from '@graphql/__generated/graphql'
import { GET_ORDER_BY_EVENT_ID } from '@graphql/events/GetOrderByEventId'
import { GET_ORDER_BY_ID } from '@graphql/events/GetOrderById'
import { SET_USER_DATA } from '@graphql/users/setUserData'
import { useTheme } from '@libs/useTheme'

type EventAttendeeInfo = EventAttendee & { additionalTicketQuantity?: number }

type AttendeeIdentity = {
  email?: string | null
  ticketTitle?: string | null
  orderId?: string | null
  ticketCode?: string | null
}

const identityForAttendee = (attendee: AttendeeIdentity) =>
  attendee.ticketCode ?? attendee.email ?? attendee.orderId ?? ''

const DetailItem = ({
  icon,
  label,
  value,
  mono = false,
  style,
}: {
  icon: ReactNode
  label: string
  value: ReactNode
  mono?: boolean
  style?: CSSProperties
}) => (
  <Box className="min-w-0 rounded-xl border p-3" style={style}>
    <Box className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-70">
      <span aria-hidden="true" className="text-base">
        {icon}
      </span>
      {label}
    </Box>
    <Box
      className={`mt-2 break-words text-sm font-medium ${
        mono ? 'font-mono' : ''
      }`}
    >
      {value || 'Not available'}
    </Box>
  </Box>
)

const AttendeeModal = ({
  eventId,
  show,
  setShow,
  attendee,
  attendees,
  onCheckInChange,
  refetchRegFields,
  regFieldsLoading,
  registrationFields,
  userRegistrationFields,
}: {
  eventId: string
  show: boolean
  setShow: (show: boolean) => void
  onCheckInChange: (
    attendee: EventAttendeeInfo,
    checkInStatus: string
  ) => void | Promise<void>
  refetchRegFields: () => void
  regFieldsLoading: boolean
  registrationFields: RegistrationField[]
  userRegistrationFields: {
    email?: string
    registrationFields?: Record<string, unknown>
  }[]
  attendee?: EventAttendeeInfo
  attendees: EventAttendeeInfo[]
}) => {
  const { theme } = useTheme()
  const [setUserData] = useMutation(SET_USER_DATA)
  const [editableFieldData, setEditableFieldData] = useState<
    (RegistrationField & { answer: unknown }) | undefined
  >()
  const [isEditModalOpen, setEditModalOpen] = useState(false)
  const [data, setData] = useState<TicketOrder>()
  const [checkInLoadingIdentity, setCheckInLoadingIdentity] =
    useState<string>()

  useEffect(() => {
    setData(undefined)
    setCheckInLoadingIdentity(undefined)
  }, [attendee?.ticketCode, attendee?.email, attendee?.orderId])

  useQuery(GET_ORDER_BY_ID as any, {
    variables: { orderId: attendee?.orderId },
    skip: !attendee?.orderId,
    fetchPolicy: 'network-only',
    onCompleted: (response) => setData(response.getOrderById),
  })

  useQuery(GET_ORDER_BY_EVENT_ID, {
    variables: { eventId, userId: attendee?.email ?? '' },
    skip: !eventId || !attendee?.email || !!attendee.orderId,
    fetchPolicy: 'network-only',
    onCompleted: (response) =>
      setData(response?.getOrderByEventId as TicketOrder),
  })

  const surfaceStyle = useMemo(
    () => ({
      backgroundColor: theme.backgroundColourSecondary,
      borderColor: theme.borderColour,
      color: theme.textColour,
    }),
    [theme]
  )

  if (!attendee) return null

  const handleCheckInChange = async (
    targetAttendee: EventAttendeeInfo,
    checkInStatus: string
  ) => {
    setCheckInLoadingIdentity(identityForAttendee(targetAttendee))
    try {
      await onCheckInChange(targetAttendee, checkInStatus)
    } finally {
      setCheckInLoadingIdentity(undefined)
    }
  }

  const renderCheckInControl = (
    targetAttendee?: EventAttendeeInfo,
    fullWidth = false
  ) => {
    if (!targetAttendee)
      return (
        <span className="text-sm text-slate-600 dark:text-slate-300">
          Attendee unavailable
        </span>
      )

    const isPresent = targetAttendee.checkInStatus === 'present'
    const isLoading =
      checkInLoadingIdentity === identityForAttendee(targetAttendee)

    return (
      <Box
        className={`flex flex-wrap items-center gap-2 ${
          fullWidth ? 'w-full justify-between' : ''
        }`}
      >
        <span
          className={`inline-flex min-h-8 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
            isPresent
              ? 'border-emerald-200 bg-emerald-100 text-emerald-800'
              : 'border-slate-300 bg-slate-100 text-slate-700'
          }`}
        >
          {isPresent ? (
            <IoCheckmarkCircleOutline aria-hidden="true" className="h-4 w-4" />
          ) : (
            <IoTimeOutline aria-hidden="true" className="h-4 w-4" />
          )}
          {isPresent ? 'Checked in' : 'Not checked in'}
        </span>
        <button
          type="button"
          disabled={isLoading}
          className={`inline-flex min-h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forumm-blue disabled:cursor-not-allowed disabled:opacity-60 ${
            isPresent
              ? 'border-slate-300 bg-white text-slate-800 hover:bg-slate-100'
              : 'border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800'
          }`}
          onClick={() =>
            handleCheckInChange(targetAttendee, isPresent ? '' : 'present')
          }
        >
          {isLoading ? (
            <LoadingSpinner size="small" />
          ) : isPresent ? (
            <IoArrowUndoOutline aria-hidden="true" className="h-4 w-4" />
          ) : (
            <IoCheckmarkCircleOutline aria-hidden="true" className="h-4 w-4" />
          )}
          {isPresent ? 'Undo check-in' : 'Check in'}
        </button>
      </Box>
    )
  }

  const getRegistrationFieldAnswer = (field: RegistrationField) => {
    const answers = userRegistrationFields.find(
      (entry) => entry.email === attendee.email
    )?.registrationFields
    const answerKey = answers
      ? Object.keys(answers).find(
          (key) => key.toLowerCase() === field.name.toLowerCase()
        )
      : undefined
    const value = answerKey ? answers?.[answerKey] : undefined

    if (value === undefined || value === null || value === '') return '—'
    if (field.type === FormInputType.Date) {
      const date = moment(value as moment.MomentInput)
      if (!date.isValid()) return String(value)
      return field.dateOnly
        ? date.format('DD/MM/YYYY')
        : date.format('DD/MM/YYYY HH:mm')
    }
    return String(value)
  }

  const handleFieldAnswerChange = async () => {
    const userId = attendee.userId ?? attendee.email
    if (!userId || !editableFieldData?.name) return

    const response = await setUserData({
      variables: {
        input: {
          userId,
          eventId,
          values: [
            {
              name: editableFieldData.name,
              value: String(editableFieldData.answer ?? ''),
            },
          ],
        },
      },
    }).then((result) => result.data?.setUserData)

    if (response) {
      toast.success('Registration answer updated.')
      setEditModalOpen(false)
      refetchRegFields()
    } else {
      toast.error('Could not update the registration answer.')
    }
  }

  const copyText = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success(`${label} copied.`)
    } catch (_) {
      toast.error(`Could not copy ${label.toLowerCase()}.`)
    }
  }

  const orderTickets = data?.tickets ?? []
  const orderAttendees = attendee.orderId
    ? attendees.filter((candidate) => candidate.orderId === attendee.orderId)
    : [attendee]
  const selectedOrderTicket = orderTickets.find(
    (ticket) =>
      (ticket.ticketCode && ticket.ticketCode === attendee.ticketCode) ||
      (ticket.email &&
        attendee.email &&
        ticket.email.toLowerCase() === attendee.email.toLowerCase() &&
        ticket.title.toLowerCase() === attendee.ticketTitle?.toLowerCase())
  )
  const discountCode = selectedOrderTicket?.discountCode
  const isPresent = attendee.checkInStatus === 'present'

  return (
    <>
      <Modal
        title="Edit registration answer"
        show={isEditModalOpen}
        setShow={setEditModalOpen}
        zIndex="110"
        className="!max-w-xl md:!min-w-0"
      >
        <Box className="space-y-4 pb-2">
          <InputPicker
            ukey="registration_field_input"
            type={editableFieldData?.type ?? FormInputType.Text}
            dateOnly={editableFieldData?.dateOnly ?? false}
            validations={{
              minLength: {
                value: 1,
                message: 'The answer must contain at least one character',
              },
            }}
            value={editableFieldData?.answer ?? ''}
            onChange={(answer) =>
              setEditableFieldData((current) =>
                current ? { ...current, answer } : current
              )
            }
            required={editableFieldData?.required ?? false}
            label={editableFieldData?.name}
            placeholder={`Enter ${editableFieldData?.name ?? 'answer'}`}
            options={editableFieldData?.options ?? []}
          />
          <Box className="flex justify-end gap-2">
            <Button
              onClick={() => setEditModalOpen(false)}
              title="Cancel"
              type="secondary"
              size="small"
            />
            <Button
              onClick={handleFieldAnswerChange}
              title="Save answer"
              type="primary"
              textColor="white"
              size="small"
            />
          </Box>
        </Box>
      </Modal>

      <Modal
        title="Attendee information"
        show={show}
        setShow={setShow}
        className="!max-w-5xl md:!min-w-0"
      >
        <Box className="space-y-5 pb-2">
          <section
            className="rounded-2xl border p-4 sm:p-5"
            style={surfaceStyle}
          >
            <Box className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <Box className="flex min-w-0 items-center gap-4">
                <Box className="shrink-0">
                  <ProfileImage
                    size="lg"
                    key={identityForAttendee(attendee)}
                    imageUrl={attendee.profileImage}
                  />
                </Box>
                <Box className="min-w-0">
                  <Box className="truncate text-xl font-semibold sm:text-2xl">
                    {attendee.name || 'Unnamed attendee'}
                  </Box>
                  {attendee.email && !attendee.isAdditionalGuest ? (
                    <a
                      href={`mailto:${attendee.email}`}
                      className="mt-1 flex items-center gap-1.5 break-all text-sm text-forumm-blue hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forumm-blue"
                    >
                      <IoMailOutline aria-hidden="true" className="h-4 w-4 shrink-0" />
                      {attendee.email}
                    </a>
                  ) : (
                    <Box className="mt-1 text-sm opacity-70">
                      Managed by registrar
                    </Box>
                  )}
                  <Box className="mt-3 flex flex-wrap gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                        attendee.registered
                          ? 'border-blue-200 bg-blue-100 text-blue-800'
                          : 'border-amber-200 bg-amber-100 text-amber-900'
                      }`}
                    >
                      {attendee.registered ? (
                        <IoCheckmarkCircleOutline aria-hidden="true" />
                      ) : (
                        <IoCloseCircleOutline aria-hidden="true" />
                      )}
                      {attendee.registered ? 'Registered' : 'Invitation pending'}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                        isPresent
                          ? 'border-emerald-200 bg-emerald-100 text-emerald-800'
                          : 'border-slate-300 bg-slate-100 text-slate-700'
                      }`}
                    >
                      {isPresent ? (
                        <IoCheckmarkCircleOutline aria-hidden="true" />
                      ) : (
                        <IoTimeOutline aria-hidden="true" />
                      )}
                      {isPresent ? 'Checked in' : 'Not checked in'}
                    </span>
                  </Box>
                </Box>
              </Box>
              <Box className="w-full lg:w-auto">
                {renderCheckInControl(attendee, true)}
              </Box>
            </Box>
          </section>

          <section aria-labelledby="ticket-details-heading">
            <Box className="mb-3 flex items-center justify-between">
              <Box id="ticket-details-heading" className="text-base font-semibold">
                Ticket details
              </Box>
              {attendee.email && !attendee.isAdditionalGuest && (
                <button
                  type="button"
                  className="inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold text-forumm-blue transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forumm-blue"
                  onClick={() => copyText(attendee.email!, 'Email')}
                >
                  <IoCopyOutline aria-hidden="true" className="h-4 w-4" />
                  Copy email
                </button>
              )}
            </Box>
            <Box className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <DetailItem
                icon={<IoTicketOutline />}
                label="Ticket type"
                value={attendee.ticketTitle}
                style={surfaceStyle}
              />
              <DetailItem
                icon={<IoTicketOutline />}
                label="Ticket code"
                value={attendee.ticketCode}
                mono
                style={surfaceStyle}
              />
              <DetailItem
                icon={<IoTicketOutline />}
                label="Discount code"
                value={discountCode ?? 'None'}
                mono={!!discountCode}
                style={surfaceStyle}
              />
              <DetailItem
                icon={<IoTimeOutline />}
                label="Last check-in activity"
                value={
                  attendee.checkInDatetime
                    ? moment(attendee.checkInDatetime).format(
                        'DD MMM YYYY, HH:mm'
                      )
                    : 'No activity yet'
                }
                style={surfaceStyle}
              />
              <DetailItem
                icon={<IoTimeOutline />}
                label="Order date"
                value={
                  attendee.invitationSentDatetime
                    ? moment(attendee.invitationSentDatetime).format(
                        'DD MMM YYYY, HH:mm'
                      )
                    : 'Not available'
                }
                style={surfaceStyle}
              />
              <DetailItem
                icon={<IoPersonOutline />}
                label="Order ID"
                value={attendee.orderId}
                mono
                style={surfaceStyle}
              />
            </Box>
          </section>

          {orderAttendees.length > 0 && (
            <section aria-labelledby="order-tickets-heading">
              <Box className="mb-3">
                <Box id="order-tickets-heading" className="text-base font-semibold">
                  Tickets in this order
                </Box>
                <Box className="mt-1 text-sm opacity-70">
                  Check in every guest independently from the primary ticket
                  holder.
                </Box>
              </Box>
              <Box className="space-y-2">
                {orderAttendees.map((ticketAttendee, index) => {
                  return (
                    <Box
                      key={identityForAttendee(ticketAttendee)}
                      className="grid gap-3 rounded-xl border p-3 sm:grid-cols-[minmax(0,1fr)_minmax(140px,0.7fr)_auto] sm:items-center"
                      style={surfaceStyle}
                    >
                      <Box className="min-w-0">
                        <Box className="flex flex-wrap items-center gap-2">
                          <Box className="truncate text-sm font-semibold">
                            {ticketAttendee.name || 'Unnamed attendee'}
                          </Box>
                          {index === 0 && (
                            <span className="rounded-full border border-blue-200 bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-800">
                              Primary
                            </span>
                          )}
                        </Box>
                        <Box className="mt-1 truncate text-xs opacity-70">
                          {ticketAttendee.isAdditionalGuest
                            ? 'Managed by registrar'
                            : ticketAttendee.email ?? '—'}
                        </Box>
                      </Box>
                      <Box className="text-sm font-medium">
                        {ticketAttendee.ticketTitle}
                      </Box>
                      <Box>{renderCheckInControl(ticketAttendee)}</Box>
                    </Box>
                  )
                })}
              </Box>
            </section>
          )}

          {!attendee.isAdditionalGuest && registrationFields.length > 0 && (
            <section aria-labelledby="registration-fields-heading">
              <Box
                id="registration-fields-heading"
                className="mb-3 text-base font-semibold"
              >
                Registration information
              </Box>
              <Box
                className="overflow-hidden rounded-xl border"
                style={{ borderColor: theme.borderColour }}
              >
                {registrationFields.map((field) => {
                  const answer = getRegistrationFieldAnswer(field)
                  return (
                    <Box
                      key={field.name}
                      className="flex flex-col gap-2 border-b p-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
                      style={surfaceStyle}
                    >
                      <Box className="min-w-0">
                        <Box className="text-xs font-semibold uppercase tracking-wide opacity-70">
                          {field.name}
                        </Box>
                        <Box className="mt-1 break-words text-sm">
                          {regFieldsLoading ? (
                            <LoadingSpinner size="small" />
                          ) : (
                            answer
                          )}
                        </Box>
                      </Box>
                      <Button
                        onClick={() => {
                          setEditableFieldData({ ...field, answer })
                          setEditModalOpen(true)
                        }}
                        title="Edit"
                        type="secondary"
                        size="small"
                        disabled={regFieldsLoading}
                        className="shrink-0"
                      />
                    </Box>
                  )
                })}
              </Box>
            </section>
          )}
        </Box>
      </Modal>
    </>
  )
}

export default AttendeeModal
